import { Skeleton as MuiSkeleton, Stack as MuiStack } from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Theme } from "common";
import StudyRecruitmentSubheader from "./StudyRecruitmentSubheader";
import StudyRecruitmentStatsPanel from "./StudyRecruitmentStatsPanel";
import StudyRecruitmentOutreach from "./StudyRecruitmentOutreach";
import StudyRecruitmentForm from "./StudyRecruitmentForm";
import { LOADER_TYPES } from "app/services/StudyRecruitmentService/constants";
import { useStudyRecruitment } from "app/services/StudyRecruitmentService/StudyRecruitmentProvider";

const boxStyles = {
  padding: "16px",
  backgroundColor: Theme.palette.primary.contrastText,
  borderRadius: "8px",
  boxShadow: "rgba(0, 0, 0, 0.1) 1px 1px 3px 0.2px ",
  width: "1150px",
};

const StudyRecruitmentFormContainer = () => {
  const { t } = useTranslation();
  const { studyRecruitmentData, saveStudyRecruitmentForm, isLoading } =
    useStudyRecruitment();

  const studyDataLoading = isLoading[LOADER_TYPES.STUDY_RECRUITMENT_DATA];
  const statsLoading = isLoading[LOADER_TYPES.STUDY_RECRUITMENT_METRICS];
  const outreachLoading =
    isLoading[LOADER_TYPES.OUTREACH_COHORTS] ||
    isLoading[LOADER_TYPES.STUDY_RECRUITMENT_METRICS];

  const yupSchema = Yup.object({
    name: Yup.string().nullable().required(t("formValidation.requiredField")),
    email: Yup.string()
      .email(t("formValidation.invalidEmail"))
      .max(10000, t("formValidation.maxLength", { max: 10000 }))
      .required(t("formValidation.requiredField")),
    phone: Yup.string()
      .nullable()
      .max(10, t("formValidation.maxLength", { max: 10 }))
      .matches(/^[0-9]{10}$/, t("formValidation.invalidPhoneNumber")),
    studyTitle: Yup.string()
      .max(900, t("formValidation.maxLength", { max: 900 }))
      .required(t("formValidation.requiredField")),
    purpose: Yup.string()
      .max(10000, t("formValidation.maxLength", { max: 10000 }))
      .required(t("formValidation.requiredField")),
    inclusionCriteria: Yup.string()
      .max(10000, t("formValidation.maxLength", { max: 10000 }))
      .required(t("formValidation.requiredField")),
    exclusionCriteria: Yup.string()
      .max(10000, t("formValidation.maxLength", { max: 10000 }))
      .required(t("formValidation.requiredField")),
    prescreeningQuestionnaireUrl: Yup.string()
      .nullable()
      .max(10000, t("formValidation.maxLength", { max: 10000 }))
      .url(t("formValidation.invalidUrl")),
    prescreeningQuestionnaireEstimatedTime: Yup.number()
      .nullable()
      .min(1, t("formValidation.invalidTime")),
  });

  return (
    <Formik
      initialValues={studyRecruitmentData}
      validationSchema={yupSchema}
      enableReinitialize
      onSubmit={async (values) => {
        await saveStudyRecruitmentForm(studyRecruitmentData.studyId, values);
      }}
    >
      <MuiStack gap={2} sx={boxStyles}>
        <StudyRecruitmentSubheader />

        <MuiStack alignItems="flex-start" direction="row" gap={2}>
          {statsLoading ? (
            <MuiSkeleton variant="rounded" width={268} height={421} />
          ) : (
            <StudyRecruitmentStatsPanel />
          )}

          <MuiStack gap={2}>
            {outreachLoading ? (
              <MuiSkeleton variant="rounded" width={834} height={124} />
            ) : (
              <StudyRecruitmentOutreach />
            )}

            {studyDataLoading ? (
              <MuiSkeleton variant="rounded" width={834} height={815} />
            ) : (
              <Form>
                <StudyRecruitmentForm />
              </Form>
            )}
          </MuiStack>
        </MuiStack>
      </MuiStack>
    </Formik>
  );
};

export default StudyRecruitmentFormContainer;
